import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import {
  Screen,
  useNextUrlWithPendingAppInstallationId,
} from "@v2/features/account/common";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import { getPostLoginLandingPath } from "@v2/features/account/postLoginLanding";
import { isAgentEnableAuthIntent } from "@v2/features/account/signupFunnelUtils";
import { authRouter } from "@v2/trpc/routes/auth";
import { userRouter } from "@v2/trpc/routes/user";
import { storeToken } from "@v2/utils/store-token";
import { useForm, useWatch } from "react-hook-form";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import {
  clearLastVisitedDashboardRoute,
  getLastVisitedIdsFromStorage,
  setLastVisitedDashboardRouteForProject,
} from "replo-utils/lib/lastVisitedDashboardIds";
import { isEmpty } from "replo-utils/lib/misc";
import { z } from "zod";

const emailSchema = z.email();

type LoginFormValues = {
  email: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const userUtils = userRouter.useUtils();

  // NOTE (Yuxin, 2026-07-18): ?type=agent login is now a normal login; enabling Agent for an existing account lives at /beta/enable.
  // Resolve the agent landing before leaving /auth/login so agent users get one
  // document load instead of the legacy app booting just to compute the same
  // redirect (REPL-30166). `next` deep links and pending Shopify install params
  // keep the legacy landing (`useNextUrl` defaults `nextUrl` to "/" when `next`
  // is absent, so presence must be checked on the search params). `staleTime: 0`
  // bypasses the logged-out `null` that CurrentUserProvider already cached for
  // this query on the login screen. Any fetch failure falls back to the legacy
  // landing unchanged.
  const handleLogin = async (token: string) => {
    storeToken(token);
    const isLegacyLandingFlow =
      searchParams.get("next") !== null ||
      searchParams.get("shopifyIntegrationId") !== null ||
      searchParams.get("pendingAppInstallationId") !== null;
    let landingPath = nextUrl;
    if (!isLegacyLandingFlow) {
      const lastVisitedIds = getLastVisitedIdsFromStorage();
      // A same-key anonymous request may still be in flight from the login
      // screen. Cancel it so this fetch cannot dedupe onto its logged-out result.
      await userUtils.get.cancel(lastVisitedIds);
      const user = await userUtils.get
        .fetch(lastVisitedIds, { staleTime: 0 })
        .catch(() => null);
      if (user?.defaultProjectId) {
        setLastVisitedDashboardRouteForProject({
          projectId: user.defaultProjectId,
          hostingType: user.defaultProjectHostingType ?? null,
          agentEnabledAt: user.defaultProjectAgentEnabledAt ?? null,
        });
      } else {
        clearLastVisitedDashboardRoute();
      }
      landingPath = getPostLoginLandingPath(user);
    }
    const searchString = nextSearchParams.toString();
    window.location.replace(
      `${landingPath}${searchString ? `?${searchString}` : ""}`,
    );
  };

  const {
    mutate: loginApi,
    isPending: isLoadingLogin,
    error: loginError,
  } = authRouter.login.useMutation({
    onSuccess: ({ token }) => void handleLogin(token),
  });

  const { mutate: loginWithGoogleApi, error: googleLoginError } =
    authRouter.loginWithGoogle.useMutation({
      onSuccess: ({ token }) => void handleLogin(token),
    });

  // Note (Kevin, 2026-01-03, REPL-21631): Prefill test credentials on localhost
  const shouldPrefillTestCredentials = isLocalhost();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: shouldPrefillTestCredentials
        ? "test@replo.app"
        : searchParams.get("email") ?? "",
      password: shouldPrefillTestCredentials ? "12345678" : "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });

  const handleGoogleCredentials = async (
    credentialResponse: CredentialResponse,
  ) => {
    const { credential, clientId } = credentialResponse;
    if (credential && clientId) {
      const pendingTemplateId = sessionStorage.getItem(
        "replo_pending_template_id",
      );
      loginWithGoogleApi({
        credential,
        clientId,
        ...(pendingTemplateId ? { pendingTemplateId } : {}),
      });
    }
  };

  const onSubmit = async ({ email, password }: LoginFormValues) => {
    const pendingTemplateId = sessionStorage.getItem(
      "replo_pending_template_id",
    );
    loginApi({
      email,
      rawPassword: password,
      ...(pendingTemplateId ? { pendingTemplateId } : {}),
    });
  };

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;
  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const loginErrorMessage = loginError?.message ?? googleLoginError?.message;
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      <div className="mb-5 min-h-[6.5rem] flex flex-col items-center justify-center gap-2 text-center">
        {isAgentEnableAuthIntent({ pathname, searchParams }) ? (
          <>
            <h1 className="font-editorial text-3xl font-bold text-default">
              Welcome to Replo Agent Beta
            </h1>
            <p className="text-sm text-muted">
              Log in to turn on Agent on Replo
            </p>
          </>
        ) : (
          <>
            <h1 className="font-editorial text-3xl font-bold text-default">
              Sell{" "}
              <span className="font-editorial font-light italic">Anything</span>{" "}
              with Replo
            </h1>
            <p className="text-sm text-muted">
              Sign in to pick up where you left off.
            </p>
          </>
        )}
      </div>
      {(loginErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !loginErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            loginErrorMessage ??
            "Please create or log in to your Replo account to continue to Replo."
          }
        />
      )}
      <div className="flex flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            invalid={Boolean(emailError)}
            autoComplete="off"
            placeholder="Your Work Email"
            {...register("email", {
              required: "Please enter a valid email address.",
              validate: (value) => {
                const parsedEmail = emailSchema.safeParse(value);
                return (
                  parsedEmail.success || "Please enter a valid email address"
                );
              },
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("email"),
            })}
            type="email"
            size="base"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          id="password"
          labelAction={
            <Link
              to={
                watchAllFields.email
                  ? `${Screen.INIT_RESET_PASSWORD}?email=${encodeURIComponent(
                      watchAllFields.email,
                    )}`
                  : Screen.INIT_RESET_PASSWORD
              }
              className="text-primary text-sm font-medium hover:underline"
            >
              Forgot Password?
            </Link>
          }
          size="base"
          error={passwordError}
        >
          <Input
            id="password"
            autoComplete="off"
            placeholder="••••••••••••"
            invalid={Boolean(passwordError)}
            {...register("password", {
              required: "Please enter a valid password",
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("password"),
            })}
            type="password"
            size="base"
          />
        </LabeledControl>
      </div>
      <div className="mt-10 flex flex-col gap-y-4 overflow-hidden">
        <Button
          className="w-full"
          variant="primary"
          size="base"
          type="submit"
          loading={isLoadingLogin}
        >
          Login
        </Button>
        <Separator />
        <GoogleAuth handleGoogleCredentials={handleGoogleCredentials} />
      </div>
    </form>
  );
};

export default LoginForm;
